import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { startOfDay } from 'date-fns';
import { orderBy } from 'lodash-es';
import ErrorWithCountdown from '#/shared/components/ErrorWithCountdown';
import { useGetRouteAlertsQuery } from '#/services/developerApi';
import AlertsBadge from '#/transit-tracker/components/AlertsBadge';
import useIntlDatetime from '#/shared/hooks/useIntlDatetime';
import { getNewTriMetDate } from '#/shared/utils/date-time';
import * as Styled from './styled';

function RouteServiceAlerts({ routeId, children }) {
  const { milliSecondsToTriMetDateString, milliSecondsToTriMetTimeString } =
    useIntlDatetime();

  const intl = useIntl();

  const formatTimestamp = millisecondsTimeStamp => {
    const lastMidnight = startOfDay(getNewTriMetDate());
    if (millisecondsTimeStamp > lastMidnight) {
      return (
        <FormattedMessage
          id="alerts.effectivewhen"
          values={{
            when: milliSecondsToTriMetTimeString(millisecondsTimeStamp),
          }}
        />
      );
    }

    return (
      <FormattedMessage
        id="alerts.asofdate"
        values={{ date: milliSecondsToTriMetDateString(millisecondsTimeStamp) }}
      />
    );
  };

  const { data, isError, isLoading } = useGetRouteAlertsQuery(routeId);
  let routeSpecificAlerts = null;

  if (isLoading) {
    return null;
  }

  let sortedRouteSpecificAlerts;

  if (data) {
    routeSpecificAlerts = data.routeAlerts.alerts;

    sortedRouteSpecificAlerts = orderBy(
      routeSpecificAlerts,
      alert => alert.begin,
      'desc'
    );
  }

  return (
    (isError && (
      <ErrorWithCountdown errorMessageId="system-error.warning" />
    )) || (
      <Styled.AlertListWrapper>
        {children && (
          <Styled.AlertListHeader>{children}</Styled.AlertListHeader>
        )}
        <Styled.AlertListBody>
          {sortedRouteSpecificAlerts &&
            sortedRouteSpecificAlerts.map(a => (
              <Styled.AlertRow key={a.id}>
                <Styled.AlertIconColumn aria-hidden="true">
                  <AlertsBadge />
                </Styled.AlertIconColumn>
                <Styled.AlertDetailColumn>
                  <Styled.AlertContent>
                    {a.desc}
                    {a.info_link_url != null && (
                      <Styled.AlertMoreLink
                        href={a.info_link_url}
                        aria-label={intl.formatMessage({
                          id: 'alerts.more-link-aria',
                        })}
                      >
                        <FormattedMessage id="alerts.more" />
                      </Styled.AlertMoreLink>
                    )}
                  </Styled.AlertContent>
                  <Styled.AlertTimestamp>
                    {formatTimestamp(a.begin)}
                  </Styled.AlertTimestamp>
                </Styled.AlertDetailColumn>
              </Styled.AlertRow>
            ))}
          {/*
          The API does not distinguish between an empty dataset and a route
          that doesn't exist so its looks like the best can do right now is
          handle an empty dataset
        */}
          {sortedRouteSpecificAlerts &&
            sortedRouteSpecificAlerts.length === 0 && (
              <Styled.AlertRow>
                <Styled.AlertDetailColumn>
                  <Styled.AlertContent>
                    <FormattedMessage id="alerts.noroutealerts" />
                  </Styled.AlertContent>
                </Styled.AlertDetailColumn>
              </Styled.AlertRow>
            )}
        </Styled.AlertListBody>
      </Styled.AlertListWrapper>
    )
  );
}

RouteServiceAlerts.propTypes = {
  routeId: PropTypes.string,
  children: PropTypes.node,
};

RouteServiceAlerts.defaultProps = {
  routeId: null,
  children: null,
};

export default RouteServiceAlerts;
